import { createLocalClashEngine, type ClashResponseMessage } from './clashEngine'
import type {
  BroadRequest,
  BroadResponse,
  ClashEngine,
  NarrowRequest,
  NarrowResponse,
} from './clashTypes'

// ---------------------------------------------------------------------------
// Cliente do worker: a mesma interface `ClashEngine`, mas com o trabalho
// a correr fora da thread da UI.
//
// Porque interessa: a narrow-phase de um modelo a serio sao dezenas de
// segundos de matematica. Na thread principal isso e o rato preso, o
// 3D congelado e o browser a oferecer "matar a pagina". No worker, a UI
// continua a 60 fps e so recebe progresso entre lotes.
//
// Transferencia, nao copia: os TypedArrays da geometria viajam por
// transferencia de buffer (custo O(1)); a thread principal perde-os, o
// que e exactamente o que se quer (o lote seguinte volta a pedir a
// geometria ao motor Fragments). Um buffer so e transferido quando o
// array o ocupa por inteiro; vistas parciais sao copiadas, porque
// transferir o buffer inteiro levaria dados de terceiros.
//
// Cancelamento: `dispose()` mata o worker e rejeita o que estiver
// pendente. O orquestrador so precisa de deixar de pedir lotes.
// ---------------------------------------------------------------------------

export interface WorkerLike {
  postMessage: (message: unknown, transfer?: Transferable[]) => void
  terminate: () => void
  onmessage: ((ev: { data: ClashResponseMessage }) => void) | null
  onerror: ((ev: unknown) => void) | null
}

/** Cria o Worker real. Isolado numa funcao para os testes poderem
 *  injectar um duplo e para o `new URL(...)` so ser avaliado quando
 *  alguem quer mesmo um worker. */
function defaultWorkerFactory(): WorkerLike {
  return new Worker(new URL('./clashWorker.ts', import.meta.url), {
    type: 'module',
  }) as unknown as WorkerLike
}

function transferablesOf(req: NarrowRequest): Transferable[] {
  const out: Transferable[] = []
  const seen = new Set<ArrayBufferLike>()
  const add = (view: Float32Array | Uint32Array): void => {
    const buf = view.buffer
    if (view.byteOffset !== 0 || view.byteLength !== buf.byteLength) return
    if (seen.has(buf)) return
    seen.add(buf)
    out.push(buf as Transferable)
  }
  add(req.pairs)
  for (const el of req.elements) {
    add(el.positions)
    add(el.indices)
  }
  return out
}

/** Motor apoiado num Web Worker. Se o Worker nao puder ser criado
 *  (ambiente sem suporte), cai para o motor local: melhor lento do que
 *  partido. */
export function createWorkerClashEngine(
  factory: () => WorkerLike = defaultWorkerFactory,
): ClashEngine {
  let w: WorkerLike
  try {
    w = factory()
  } catch {
    return createLocalClashEngine()
  }
  let nextId = 1
  let disposed = false
  const pending = new Map<
    number,
    { resolve: (v: unknown) => void; reject: (e: Error) => void }
  >()

  w.onmessage = (ev: { data: ClashResponseMessage }) => {
    const msg = ev.data
    if (!msg || typeof msg.id !== 'number') return
    const entry = pending.get(msg.id)
    if (!entry) return
    pending.delete(msg.id)
    if (msg.type === 'error') entry.reject(new Error(msg.message))
    else entry.resolve(msg.payload)
  }
  w.onerror = () => {
    const err = new Error('clash worker failed')
    for (const [, entry] of pending) entry.reject(err)
    pending.clear()
  }

  const send = <T,>(
    type: 'broad' | 'narrow',
    payload: BroadRequest | NarrowRequest,
    transfer: Transferable[],
  ): Promise<T> => {
    if (disposed) return Promise.reject(new Error('clash engine disposed'))
    const id = nextId++
    return new Promise<T>((resolve, reject) => {
      pending.set(id, { resolve: resolve as (v: unknown) => void, reject })
      try {
        w.postMessage({ id, type, payload }, transfer)
      } catch (err) {
        pending.delete(id)
        reject(err instanceof Error ? err : new Error(String(err)))
      }
    })
  }

  return {
    broadPhase: (req: BroadRequest) =>
      send<BroadResponse>('broad', req, [
        req.boxes.buffer as Transferable,
        req.modelIndex.buffer as Transferable,
        req.categoryIndex.buffer as Transferable,
      ]),
    narrowPhase: (req: NarrowRequest) =>
      send<NarrowResponse>('narrow', req, transferablesOf(req)),
    dispose: () => {
      if (disposed) return
      disposed = true
      const err = new Error('clash engine disposed')
      for (const [, entry] of pending) entry.reject(err)
      pending.clear()
      try {
        w.terminate()
      } catch {
        // Um worker ja morto nao e problema nosso.
      }
    },
  }
}
